import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { ActiveSiteBanner } from '../components/ActiveSiteBanner';
import { useCurrentSite } from '../lib/currentSite';
import { useUserOrg } from '../lib/org';
import { getSignedUrl } from '../lib/storage';
import { listActiveSignIns } from '../lib/db';
import { getDashboardAlerts, type DashboardAlert } from '../lib/dashboardAlerts';
import { dismissAlert, getDismissedAlertIds } from '../lib/dismissedAlerts';
import { isFeatureEnabled, type FeatureKey } from '../lib/features';
import { Logo } from '../components/Logo';
import { PermitsCloseOutWidget } from '../components/PermitsCloseOutWidget';
import { LoadingIndicator } from '../components/LoadingIndicator';

interface Tile {
  featureKey?: FeatureKey;
  to: string;
  title: string;
  sub: string;
  icon: React.ReactNode;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

function ArrowIcon()    { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>; }
function CircleIcon()   { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" /></svg>; }
function DocIcon()      { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>; }
function ChatIcon()     { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>; }
function ExclaimIcon()  { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="6" x2="12" y2="14" /><line x1="12" y1="18" x2="12" y2="18" /></svg>; }
function PlusIcon()     { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function FoldedDocIcon(){ return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" /></svg>; }
// PlayIcon is unused while the How-to videos tile is hidden — kept here
// commented out so re-enabling the tile is a one-line uncomment, not a
// re-author. Restore when videos ship.
// function PlayIcon()    { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" /></svg>; }
function PermitIcon(){ return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6z" /><path d="M9 12l2 2 4-4" /></svg>; }

function TileLink({ tile }: { tile: Tile }) {
  const inner = (
    <>
      <span className={`home-tile-icon home-tile-icon-${tile.variant ?? 'default'}`}>{tile.icon}</span>
      <span className="home-tile-title">{tile.title}</span>
      <span className="home-tile-sub">{tile.sub}</span>
    </>
  );
  if (tile.disabled) {
    return <div className="home-tile home-tile-disabled" aria-disabled>{inner}</div>;
  }
  return <Link to={tile.to} className="home-tile">{inner}</Link>;
}

export function Dashboard() {
  const { currentSite, sites, loading } = useCurrentSite();
  const { org } = useUserOrg();
  const [activeCount, setActiveCount] = useState<number | null>(null);
  const [alerts, setAlerts] = useState<DashboardAlert[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => getDismissedAlertIds());
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [showSlowLoadFallback, setShowSlowLoadFallback] = useState(false);

  // Only show the 'Couldn't load' error if loading hasn't resolved within
  // 3 seconds — protects against the brief org=null gap on navigation.
  useEffect(() => {
    if (!loading && org) { setShowSlowLoadFallback(false); return; }
    const t = setTimeout(() => setShowSlowLoadFallback(true), 3000);
    return () => clearTimeout(t);
  }, [loading, org]);

  useEffect(() => {
    let cancelled = false;
    if (org?.logo_path) {
      getSignedUrl('org-logos', org.logo_path, 3600)
        .then((u) => { if (!cancelled) setLogoUrl(u); })
        .catch(() => { if (!cancelled) setLogoUrl(null); });
    } else {
      setLogoUrl(null);
    }
    return () => { cancelled = true; };
  }, [org?.logo_path]);

  // Bump this to force a refetch of activeCount + alerts. We bump it when the
  // user returns to the tab (visibilitychange / focus), so sign-in/sign-out
  // actions taken on another route reflect immediately on return — previously
  // the home tile and "No first aider" alert showed stale state until a full
  // page reload.
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    function bump() {
      if (document.visibilityState === 'visible') setRefreshKey((k) => k + 1);
    }
    document.addEventListener('visibilitychange', bump);
    window.addEventListener('focus', bump);
    return () => {
      document.removeEventListener('visibilitychange', bump);
      window.removeEventListener('focus', bump);
    };
  }, []);

  useEffect(() => {
    if (!currentSite) { setActiveCount(null); return; }
    listActiveSignIns(currentSite.id)
      .then((list) => setActiveCount(list.length))
      .catch(() => setActiveCount(null));
  }, [currentSite?.id, refreshKey]);


  useEffect(() => {
    if (!org) { setAlerts([]); return; }
    let cancelled = false;
    getDashboardAlerts(org.org_id, currentSite?.id ?? null)
      .then((list) => { if (!cancelled) setAlerts(list); })
      .catch(() => { if (!cancelled) setAlerts([]); });
    return () => { cancelled = true; };
  }, [org?.org_id, currentSite?.id, refreshKey]);

  // Auto-redirect first-time users into the welcome wizard.
  //
  // Once an org is set up (profile_completed in DB OR a site exists OR the
  // localStorage flag is set), we stop redirecting — even if iOS clears
  // localStorage, the DB state means returning users skip the wizard.
  //
  // Important: this ONLY redirects users to /welcome — it no longer also
  // bounces to /welcome/first-rams. The First-RAMS step is now handled
  // inline by the /welcome wizard's completion handler (Welcome.tsx
  // navigates straight to /welcome/first-rams as its last step). That
  // means returning users always land on Home after login, even on a
  // device with cleared localStorage or an org that has 0 RAMS yet.
  // Auto-bouncing every login is a worse UX than letting users find
  // "Create my first RAMS" themselves from the Home tiles.
  useEffect(() => {
    if (loading) return;
    if (!org) return;                           // wait for org to load
    let welcomeDone = false;
    try { welcomeDone = localStorage.getItem('hs_welcome_done') === '1'; } catch { /* noop */ }
    const orgSetUp = org.profile_completed || sites.length > 0 || welcomeDone;
    if (!orgSetUp) {
      window.location.replace('/welcome');
      return;
    }
  }, [loading, sites.length, org]);

  // Show 'Loading…' while sites/org are coming through. If org is still
  // null after 3 seconds, then we've genuinely failed to load and switch
  // to the recovery prompt — but for the normal page-to-page navigation
  // gap (which can briefly show org=null), we don't flash an error.
  // <LoadingIndicator /> handles the 300ms premium-feel delay internally,
  // so the dashboard chrome (header, tab bar) appears instantly and only
  // shows a spinner for genuinely slow loads.
  if (loading || !org) {
    return (
      <AppShell documentTitle="Home">
        {showSlowLoadFallback ? (
          <div className="card" style={{ textAlign: 'center', marginTop: 32 }}>
            <h2 style={{ marginTop: 0 }}>Couldn't load your account</h2>
            <p className="muted">Try signing out and back in. If it keeps happening, drop us a note.</p>
            <Link to="/settings" className="btn btn-primary" style={{ display: 'inline-block', textDecoration: 'none', marginTop: 12 }}>Open Settings</Link>
          </div>
        ) : (
          <LoadingIndicator />
        )}
      </AppShell>
    );
  }

  // Note: we deliberately don't render a separate empty-state screen here.
  // - First-time users (no profile, no sites) are redirected to /welcome
  //   by the effect above.
  // - Returning users with no active site get a friendly 'Tap to choose or
  //   add one' card from ActiveSiteBanner — far less jarring than a full
  //   replacement screen, and avoids the transient flash.

  const siteId = currentSite?.id;
  const onSiteSub =
    activeCount === null ? 'Tap to manage' :
    activeCount === 0 ? 'Nobody on site' :
    activeCount === 1 ? '1 person on site' :
    `${activeCount} people on site`;

  const tiles: Tile[] = [
    {
      to: siteId ? `/sites/${siteId}` : '/sites',
      title: 'Sign in / out',
      sub: onSiteSub,
      icon: <ArrowIcon />,
    },
    {
      to: siteId ? `/sites/${siteId}/checks` : '/sites',
      title: 'Tick lists',
      sub: 'Daily · Weekly · Monthly',
      icon: <CircleIcon />,
    },
    {
      to: siteId ? `/sites/${siteId}/rams` : '/sites',
      title: 'RAMS library',
      sub: 'Risk Assessment & Method Statements — per-site, by company',
      icon: <DocIcon />,
    },
    {
      to: siteId ? `/sites/${siteId}/forms/toolbox_talk` : '/sites',
      title: 'Toolbox talk',
      sub: 'Record + sign on',
      icon: <ChatIcon />,
    },
    {
      to: siteId ? `/sites/${siteId}/forms/accident` : '/sites',
      title: 'Incident',
      sub: 'Log an accident or near-miss',
      icon: <ExclaimIcon />,
      variant: 'danger',
    },
    {
      to: siteId ? `/sites/${siteId}/forms/subcontractor_induction` : '/sites',
      title: 'Subbie induction',
      sub: 'Induct a new person',
      icon: <PlusIcon />,
      featureKey: 'subbies',
    },
    {
      to: '/documents',
      title: 'Documents',
      sub: 'CDM (Construction Design & Management) templates & policies',
      icon: <FoldedDocIcon />,
    },
    {
      to: siteId ? `/sites/${siteId}/wrap-up` : '/sites',
      title: 'Weekly review',
      sub: 'This week at a glance · hours · expiries',
      icon: <CircleIcon />,
    },
    {
      to: siteId ? `/sites/${siteId}/permits` : '/sites',
      title: 'Permits',
      sub: 'Hot works, dig, height, confined',
      icon: <PermitIcon />,
      variant: 'danger',
      featureKey: 'permits',
    },
    // How-to videos tile — hidden from the Dashboard until we've actually
    // recorded the videos. Showing a "coming soon" placeholder on the home
    // screen of a paid product undermines the polish. The /how-to route
    // still exists so old bookmarks don't 404; we just don't surface it
    // until there's something useful behind it.
    // {
    //   to: '/how-to',
    //   title: 'How-to videos',
    //   sub: 'Quick guides — coming soon',
    //   icon: <PlayIcon />,
    // },
  ];

  const visibleTiles = tiles.filter((t) => !t.featureKey || isFeatureEnabled(org, t.featureKey));

  return (
    <AppShell documentTitle={currentSite ? `Home · ${currentSite.name}` : 'Home'}>
      {org && (
        <div className="brand-block brand-block-saas">
          <div className="brand-logo-image brand-logo-saas">
            <Logo size={64} decorative />
          </div>
          <p className="brand-wordmark">Safe and Sorted</p>
          <p className="brand-tagline">Health &amp; safety for <strong>{org.org_name}</strong></p>
          <Link to="/settings" className="brand-block-edit" aria-label="Open Settings — your logo appears on every PDF">
            {logoUrl ? '✓ Your logo is on every PDF · Edit →' : 'Add your company logo for PDFs →'}
          </Link>
        </div>
      )}

      <ActiveSiteBanner site={currentSite} />

      {(() => {
        // Snooze any alert the user has tapped × on. 24h snooze — same id
        // comes back tomorrow if the underlying condition still holds.
        const visibleAlerts = alerts.filter((a) => !dismissedIds.has(a.id));
        if (visibleAlerts.length === 0) return null;
        function handleDismiss(id: string) {
          dismissAlert(id);
          setDismissedIds((prev) => new Set(prev).add(id));
        }
        return (
          <div className="home-alerts" style={{ margin: '0 0 16px' }}>
            {visibleAlerts.map((a) => {
              const inner = (
                <>
                  <span className="home-alert-dot" aria-hidden>{a.severity === 'red' ? '🔴' : '🟡'}</span>
                  <span className="home-alert-text">
                    <strong className="home-alert-title">{a.title}</strong>
                    <span className="home-alert-body">{a.body}</span>
                  </span>
                  {a.actionLabel && <span className="home-alert-action">{a.actionLabel} →</span>}
                </>
              );
              const cls = `home-alert home-alert-${a.severity}`;
              // The whole banner is tappable (it's the primary action), and
              // the dismiss × is a button absolutely-positioned on top of the
              // link. stopPropagation on the dismiss prevents the link from
              // also firing when the user taps ×.
              const dismissBtn = (
                <button
                  type="button"
                  className="home-alert-dismiss"
                  aria-label="Dismiss this banner for 24 hours"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDismiss(a.id);
                  }}
                >
                  ×
                </button>
              );
              // Internal links use React Router so we don't trigger a full page
              // reload (which would replay splash + welcome + site picker).
              if (a.href && a.href.startsWith('/')) {
                return (
                  <div key={a.id} className="home-alert-wrap">
                    <Link to={a.href} className={cls}>{inner}</Link>
                    {dismissBtn}
                  </div>
                );
              }
              return (
                <div key={a.id} className="home-alert-wrap">
                  <a href={a.href ?? '#'} className={cls}
                     onClick={(e) => { if (!a.href) e.preventDefault(); }}>
                    {inner}
                  </a>
                  {dismissBtn}
                </div>
              );
            })}
          </div>
        );
      })()}

      <PermitsCloseOutWidget siteId={currentSite?.id ?? null} />

      <div className="home-tile-grid">
        {visibleTiles.map((t, i) => <TileLink key={i} tile={t} />)}
      </div>

      {org?.profile_completed && (
        <Link to="/settings/features" className="show-all-tools-link">
          + Need something not here? Show all H&amp;S tools
        </Link>
      )}
    </AppShell>
  );
}
